import { useEffect, useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { DateRangeFilter, filterSelectClass, type DateRange, type RangePreset } from "@/components/reports/DateRangeFilter";
import type { ReportUser } from "@/types/report";

/** Filters a report can offer besides the period */
export type FilterKey = "head" | "user" | "lead_status" | "lead_source" | "priority" | "outcome" | "pipeline" | "product" | "quote_status" | "assign_type";
export type FilterValues = Record<FilterKey, string>;
export const EMPTY_FILTERS: FilterValues = { head: "", user: "", lead_status: "", lead_source: "", priority: "", outcome: "", pipeline: "", product: "", quote_status: "", assign_type: "" };
/** URL / API parameter for each filter */
export const FILTER_PARAM: Record<FilterKey, string> = { head: "head_id", user: "user_id", lead_status: "lead_status", lead_source: "lead_source", priority: "priority", outcome: "outcome", pipeline: "pipeline_id", product: "product_id", quote_status: "quote_status", assign_type: "assign_type" };

const LEAD_STATUSES: [string, string][] = [["NEW", "New"], ["CONTACTED", "Contacted"], ["FOLLOW_UP", "Follow-up"], ["QUALIFIED", "Qualified"], ["CONVERTED", "Converted"], ["DISQUALIFIED", "Disqualified"]];
const PRIORITIES: [string, string][] = [["HOT", "Hot"], ["WARM", "Warm"], ["COLD", "Cold"]];
const QUOTE_STATUSES: [string, string][] = [["DRAFT", "Draft"], ["PENDING_APPROVAL", "Waiting for approval"], ["APPROVED", "Approved"], ["SENT", "Sent"], ["ACCEPTED", "Accepted"], ["REJECTED", "Rejected"]];
/** How a lead was assigned (the server groups the saved reasons the same way) */
export const ASSIGN_TYPES: [string, string][] = [["AUTO", "Automatic"], ["MANUAL", "By a person"], ["POOL", "From the pool"], ["IMPORT", "Import"]];
const OPEN_LEAD_STATUSES = LEAD_STATUSES.filter(([v]) => v !== "CONVERTED" && v !== "DISQUALIFIED");
export const OUTCOMES: [string, string][] = [["INTERESTED", "Interested"], ["CALL_BACK", "Call back later"], ["NOT_INTERESTED", "Not interested"], ["NO_ANSWER", "No answer"], ["BUSY", "Busy"], ["VOICEMAIL", "Voicemail"], ["WRONG_NUMBER", "Wrong number"], ["DO_NOT_CALL", "Do not call"], ["NONE", "No outcome chosen"]];

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1 sm:flex-none">
      <label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * The filters above a report: the period, then team / salesperson and the report's own filters. The report on
 * screen and its PDF / Excel download both read these values, so the file always matches the screen.
 */
export function ReportFilterBar({
  date,
  show,
  values,
  onChange,
  onClear,
  heads,
  people,
  openLeadsOnly,
}: {
  /** The report only holds open leads, so Lead status offers only the open statuses */
  openLeadsOnly?: boolean;
  /** The period picker; leave out for a report that has its own (Overview) or shows current state */
  date?: { preset: RangePreset; range: DateRange; onPreset: (p: RangePreset) => void; onRange: (r: DateRange) => void; isDefault: boolean } | null;
  show: FilterKey[];
  values: FilterValues;
  onChange: (key: FilterKey, value: string) => void;
  onClear: () => void;
  /** Sales Heads (Super Admin's team filter) */
  heads: ReportUser[];
  /** Salespeople the caller can see */
  people: ReportUser[];
}) {
  const [sources, setSources] = useState<{ code: string; name: string }[]>([]);
  const [pipelines, setPipelines] = useState<{ id: string; name: string }[]>([]);
  const [products, setProducts] = useState<{ id: string; name: string; code?: string }[]>([]);
  const wantsSources = show.includes("lead_source");
  const wantsPipelines = show.includes("pipeline");
  const wantsProducts = show.includes("product");
  useEffect(() => {
    if (!wantsSources || sources.length) return;
    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((r) => setSources(unwrapArray<{ code: string; name: string }>(r)))
      .catch(() => setSources([]));
  }, [wantsSources, sources.length]);
  useEffect(() => {
    if (!wantsPipelines || pipelines.length) return;
    api
      .get("/api/Pipeline/List")
      .then((r) => setPipelines(unwrapArray<{ id: string; name: string }>(r)))
      .catch(() => setPipelines([]));
  }, [wantsPipelines, pipelines.length]);
  useEffect(() => {
    if (!wantsProducts || products.length) return;
    // A searchable list of up to one page of products; people without product access just don't get this filter
    api
      .get("/api/Product/List?page_size=500")
      .then((r) => {
        const o = unwrapObject<any>(r);
        setProducts(Array.isArray(o?.rows) ? o.rows : unwrapArray(r));
      })
      .catch(() => setProducts([]));
  }, [wantsProducts, products.length]);

  const visible = show.filter((k) =>
    k === "head" ? heads.length > 0 : k === "user" ? people.length > 1 : k === "pipeline" ? pipelines.length > 1 : k === "product" ? products.length > 0 : true,
  );
  const active = visible.filter((k) => values[k]).length;
  const dirty = active > 0 || (date ? !date.isDefault : false);
  // People in the chosen team (the head and everyone reporting to them)
  const teamPeople = values.head ? people.filter((p) => p.id === values.head || p.manager_id === values.head) : people;

  const select = (key: FilterKey, label: string, all: string, options: [string, string][]) => (
    <Field key={key} id={`rf-${key}`} label={label}>
      <SearchableSelect id={`rf-${key}`} className={filterSelectClass} value={values[key]} onChange={(e) => onChange(key, e.target.value)}>
        <option value="">{all}</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </SearchableSelect>
    </Field>
  );

  if (!date && !visible.length) return null;
  return (
    <section aria-label="Report filters" className="flex flex-wrap items-end gap-x-4 gap-y-3 rounded-xl border border-border bg-card px-4 py-3 shadow-xs">
      <span className="hidden items-center gap-1.5 self-center text-xs font-semibold text-muted-foreground lg:flex">
        <Filter className="size-3.5" />
        Filters
        {active > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{active}</span>}
      </span>
      {date && (
        <div className="flex max-w-full flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Period</span>
          <DateRangeFilter preset={date.preset} range={date.range} onPreset={date.onPreset} onRange={date.onRange} />
        </div>
      )}
      {visible.map((k) => {
        switch (k) {
          case "head":
            return select("head", "Team", "All teams", heads.map((h) => [h.id, h.name]));
          case "user":
            return select("user", "Salesperson", values.head ? "Everyone in the team" : "Everyone", teamPeople.map((p) => [p.id, p.active ? p.name : `${p.name} (inactive)`]));
          case "lead_status":
            return select("lead_status", "Lead status", openLeadsOnly ? "Any open status" : "Any status", openLeadsOnly ? OPEN_LEAD_STATUSES : LEAD_STATUSES);
          case "lead_source":
            return select("lead_source", "Lead source", "Any source", sources.map((s) => [s.code, s.name]));
          case "priority":
            return select("priority", "Priority", "Any priority", PRIORITIES);
          case "outcome":
            return select("outcome", "Call result", "Any result", OUTCOMES);
          case "pipeline":
            return select("pipeline", "Pipeline", "All pipelines", pipelines.map((p) => [p.id, p.name]));
          case "product":
            return select("product", "Product", "All products", products.map((p) => [p.id, p.code ? `${p.name} (${p.code})` : p.name]));
          case "quote_status":
            return select("quote_status", "Quote status", "Any status", QUOTE_STATUSES);
          case "assign_type":
            return select("assign_type", "Assignment type", "Any type", ASSIGN_TYPES);
        }
      })}
      {dirty && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onClear}>
          <X className="size-4" />
          Clear filters
        </Button>
      )}
    </section>
  );
}
